import { clearStoredChatSessionId } from '@/lib/chatbotAsk';
import { fetchChatbotBrief } from '@/lib/chatbotBrief';
import { fetchChatbotTriggers } from '@/lib/chatbotTriggers';
import { invalidateAllSalesMapCache } from '@/lib/salesMapApi';

const briefCache = new Map();
const triggersCache = new Map();

const LEGACY_CONVERSATION_KEY_PREFIX = 'neo-chat-';
const CACHE_TTL_MS = 30_000;

export function chatbotCacheKey(tenantId, userId, namespace, language) {
  const tenant = String(tenantId ?? '').trim().toLowerCase() || 'default';
  const user = String(userId ?? '').trim() || 'anonymous';
  const lang = String(language ?? '').trim() || 'en';
  return `${namespace}::${tenant}::${user}::${lang}`;
}

export function invalidateChatbotCaches() {
  briefCache.clear();
  triggersCache.clear();
}

function deleteCacheEntriesForTenantUser(cache, namespace, tenantId, userId) {
  const tenant = String(tenantId ?? '').trim().toLowerCase() || 'default';
  const user = String(userId ?? '').trim() || 'anonymous';
  const prefix = `${namespace}::${tenant}::${user}::`;
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

export function invalidateChatbotCachesForTenant(tenantId, userId) {
  deleteCacheEntriesForTenantUser(briefCache, 'brief', tenantId, userId);
  deleteCacheEntriesForTenantUser(triggersCache, 'triggers', tenantId, userId);
}

export function resetChatbotSession(tenantId, userId) {
  if (!userId) return;
  clearStoredChatSessionId(tenantId, userId);
}

export function handleChatbotTenantSwitch({ previousTenantId, nextTenantId, userId }) {
  invalidateChatbotCaches();
  invalidateAllSalesMapCache();
  if (userId && previousTenantId) {
    invalidateChatbotCachesForTenant(previousTenantId, userId);
  }
  if (userId && nextTenantId) {
    invalidateChatbotCachesForTenant(nextTenantId, userId);
    resetChatbotSession(nextTenantId, userId);
  }
  clearLegacyConversationStorage();
}

export async function fetchChatbotBriefCached(
  tenantId,
  userId,
  { bypassCache = false, language } = {},
) {
  const key = chatbotCacheKey(tenantId, userId, 'brief', language);
  if (!bypassCache) {
    const hit = briefCache.get(key);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.data;
  }
  const data = await fetchChatbotBrief(tenantId, userId, { language });
  briefCache.set(key, { data, at: Date.now() });
  return data;
}

export async function fetchChatbotTriggersCached(
  tenantId,
  userId,
  { bypassCache = false, language } = {},
) {
  const key = chatbotCacheKey(tenantId, userId, 'triggers', language);
  if (!bypassCache) {
    const hit = triggersCache.get(key);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.data;
  }
  const data = await fetchChatbotTriggers(tenantId, userId, { language });
  triggersCache.set(key, { data, at: Date.now() });
  return data;
}

export function conversationStorageKey(tenantId, userId) {
  const today = new Date();
  const day = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  const tenant = String(tenantId ?? '').trim().toLowerCase() || 'default';
  const user = String(userId ?? '').trim() || 'anonymous';
  return `neo-chat-messages::${tenant}::${user}::${day}`;
}

export function loadConversationMessages(tenantId, userId) {
  if (!tenantId || !userId) return null;
  try {
    const raw = localStorage.getItem(conversationStorageKey(tenantId, userId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (
      data?.tenantId &&
      String(data.tenantId).toLowerCase() !== String(tenantId).toLowerCase()
    ) {
      return null;
    }
    return Array.isArray(data.messages) ? data.messages : null;
  } catch {
    return null;
  }
}

export function saveConversationMessages(tenantId, userId, messages) {
  if (!tenantId || !userId) return;
  try {
    localStorage.setItem(
      conversationStorageKey(tenantId, userId),
      JSON.stringify({
        messages,
        tenantId: String(tenantId).toLowerCase(),
        userId,
        timestamp: Date.now(),
      }),
    );
  } catch {
    // ignore quota errors
  }
}

/** Remove pre-tenant-scoping conversation keys (date-only). */
export function clearLegacyConversationStorage() {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (
        key.startsWith(LEGACY_CONVERSATION_KEY_PREFIX) &&
        !key.startsWith('neo-chat-messages::') &&
        !key.startsWith('neo-chatbot-session::')
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

export function isProactiveChatMessageType(type) {
  return type === 'morning_brief' || type === 'trigger_nudges';
}

export function proactiveMessageMatchesTenant(message, tenantId) {
  if (!isProactiveChatMessageType(message?.type)) return true;
  const msgTenant = message?.tenantId ?? message?.tenant_id;
  if (!msgTenant) return false;
  return String(msgTenant).toLowerCase() === String(tenantId).toLowerCase();
}

export function stripForeignProactiveMessages(messages, tenantId) {
  return (messages || []).filter((m) => proactiveMessageMatchesTenant(m, tenantId));
}

export function filterBriefResponseItems(items, tenantId) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => {
    const itemTenant = item?.tenant_id ?? item?.tenantId;
    if (!itemTenant) return true;
    return String(itemTenant).toLowerCase() === String(tenantId).toLowerCase();
  });
}

/** Dispatched after admin-active-tenant is written to localStorage. */
export const ADMIN_TENANT_CHANGE_EVENT = 'admin-tenant-change';

export function dispatchAdminTenantChange() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(ADMIN_TENANT_CHANGE_EVENT));
}
